import { Button, Skeleton } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID } from '~/features/billing/constants'
import { usePlanProducts } from '~/features/billing/hooks/usePlanProducts'
import { trackEvent } from '~/providers'
import { formatters } from '~/utils'
import { Boundary } from './Boundary'
import { useSuspenseQuery } from '~/services'

type HelpDeskUpgradeDialogProps = {
  close: () => void
  workspaceId: string
}

export const HelpDeskUpgradeDialog = ({ close, workspaceId }: HelpDeskUpgradeDialogProps) => {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        The Help Desk is a premium feature available on paid plans. When you upgrade, you'll get access to:
      </p>
      <ul className="flex flex-col gap-4 rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Priority support from Botpress experts</p>
        </li>
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Fast, human-to-human support</p>
        </li>
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Priority bugfix escalation</p>
        </li>
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Guidance from our team of Support Engineers</p>
        </li>
      </ul>

      <p className="text-sm text-muted-foreground">
        Need help now? Check out our{' '}
        <a
          href="https://botpress.com/docs/home"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          documentation
        </a>
        ,{' '}
        <a
          href="https://discord.com/invite/botpress"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          Discord
        </a>
        , or{' '}
        <a
          href="https://www.youtube.com/botpress"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          Youtube
        </a>{' '}
        channel.
      </p>
      <Boundary suspenseFallback={<Skeleton className="h-9 w-full max-w-80 self-center rounded" />}>
        <ExpertSupportButton workspaceId={workspaceId} close={close} />
      </Boundary>
    </div>
  )
}

const ExpertSupportButton = ({ workspaceId, close }: { workspaceId: string; close: () => void }) => {
  const { planProducts } = usePlanProducts(workspaceId)
  const {
    data: { billingVersion },
  } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId })
  const isLegacyPricing = billingVersion !== 'v3'
  // Uses team plan pricing on v1/v2 billing because Plus plan doesn't exist yet
  const plusPlanPrice = planProducts.find((product) =>
    isLegacyPricing ? product.id === TEAM_PLAN_PRODUCT_ID : product.id === PLUS_PLAN_PRODUCT_ID
  )?.pricePerMonth

  return (
    <Link
      to="/workspaces/$workspaceId/billing"
      className="self-center"
      params={{ workspaceId }}
      onClick={() => {
        trackEvent({ type: 'get_expert_support', workspaceId })
        close()
      }}
    >
      <Button>Get expert support starting at {formatters.$centdollars(plusPlanPrice ?? 0)}/month</Button>
    </Link>
  )
}
